import React, { useState } from 'react';

import { DragDropContext, Draggable, Droppable } from '@hello-pangea/dnd';

import LeadCard from '../LeadCard';
import {
  BoardContainer,
  CardsContainer,
  Column,
  ColumnCheckbox,
  ColumnCount,
  ColumnHeader,
  ColumnHeaderLeft,
  ColumnIcon,
  ColumnTitle,
} from './KanbanBoard.styles';

const COLUMNS = [
  { id: 'new', title: 'New', color: '#4a90e2' },
  { id: 'contacted', title: 'Contacted', color: '#fd7e14' },
  { id: 'negotiation', title: 'Negotiation', color: '#9b59b6' },
  { id: 'closedWon', title: 'Closed Won', color: '#20c997' },
];

const KanbanBoard = ({ columns, setColumns }) => {
  const [selectedCards, setSelectedCards] = useState(new Set());

  const onDragEnd = (result) => {
    const { source, destination } = result;

    if (!destination) return;

    if (source.droppableId === destination.droppableId && source.index === destination.index) {
      return;
    }

    const sourceColumn = columns[source.droppableId];
    const destColumn = columns[destination.droppableId];
    const sourceItems = Array.from(sourceColumn);
    const destItems =
      source.droppableId === destination.droppableId ? sourceItems : Array.from(destColumn);

    const [movedItem] = sourceItems.splice(source.index, 1);
    movedItem.status = destination.droppableId;

    destItems.splice(destination.index, 0, movedItem);

    setColumns({
      ...columns,
      [source.droppableId]: sourceItems,
      [destination.droppableId]: destItems,
    });
  };

  const handleSelectCard = (cardId) => {
    setSelectedCards((prev) => {
      const newSelected = new Set(prev);
      if (newSelected.has(cardId)) {
        newSelected.delete(cardId);
      } else {
        newSelected.add(cardId);
      }
      return newSelected;
    });
  };

  const handleSelectAll = (columnId) => {
    const columnCards = columns[columnId] || [];
    const columnCardIds = columnCards.map((card) => card.id);
    const allSelected = columnCardIds.every((id) => selectedCards.has(id));

    setSelectedCards((prev) => {
      const newSelected = new Set(prev);
      if (allSelected) {
        columnCardIds.forEach((id) => newSelected.delete(id));
      } else {
        columnCardIds.forEach((id) => newSelected.add(id));
      }
      return newSelected;
    });
  };

  const handlePriorityChange = (cardId, newPriority) => {
    const allColumns = { ...columns };

    // Find and update the card's priority
    Object.keys(allColumns).forEach((columnId) => {
      const cardIndex = allColumns[columnId].findIndex((card) => card.id === cardId);
      if (cardIndex !== -1) {
        allColumns[columnId][cardIndex].priority = newPriority;
      }
    });

    setColumns(allColumns);
  };

  const isColumnAllSelected = (columnId) => {
    const columnCards = columns[columnId] || [];
    if (columnCards.length === 0) return false;
    return columnCards.every((card) => selectedCards.has(card.id));
  };

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <BoardContainer>
        {COLUMNS.map((column) => {
          const columnCards = columns[column.id] || [];
          const allSelected = isColumnAllSelected(column.id);

          return (
            <Column key={column.id}>
              <ColumnHeader $color={column.color}>
                <ColumnHeaderLeft>
                  <ColumnIcon $color={column.color}>
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                      <circle cx="10" cy="10" r="8" fill="currentColor" />
                    </svg>
                  </ColumnIcon>
                  <ColumnTitle>{column.title}</ColumnTitle>
                </ColumnHeaderLeft>
                <ColumnCount>{columnCards.length}</ColumnCount>
                <ColumnCheckbox
                  type="checkbox"
                  checked={allSelected}
                  onChange={() => handleSelectAll(column.id)}
                  onClick={(e) => e.stopPropagation()}
                />
              </ColumnHeader>

              <Droppable droppableId={column.id}>
                {(provided, snapshot) => (
                  <CardsContainer
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    $isDraggingOver={snapshot.isDraggingOver}
                    $color={column.color}
                  >
                    {columnCards.map((lead, index) => (
                      <Draggable key={lead.id} draggableId={lead.id} index={index}>
                        {(provided, snapshot) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                          >
                            <LeadCard
                              lead={lead}
                              isDragging={snapshot.isDragging}
                              isSelected={selectedCards.has(lead.id)}
                              onSelect={handleSelectCard}
                              onPriorityChange={handlePriorityChange}
                              statusColor={column.color}
                            />
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </CardsContainer>
                )}
              </Droppable>
            </Column>
          );
        })}
      </BoardContainer>
    </DragDropContext>
  );
};

export default KanbanBoard;
